import { useState, useRef, useEffect } from 'react';

/**
 * 메뉴 아이템 드래그 앤 드롭 순서 변경 훅
 *
 * @param {Function} onReorderComplete - 순서 변경 완료 시 호출되는 콜백
 * @returns {Object} 드래그 관련 state 및 핸들러
 */
export const useMenuDragReorder = (onReorderComplete) => {
  // 드래그 관련 state
  const [isDraggingMenu, setIsDraggingMenu] = useState(false);
  const [draggedMenuInfo, setDraggedMenuInfo] = useState(null); // { category, index, menuId/prodCd }
  const [menuDragStartY, setMenuDragStartY] = useState(0);
  // currentDragY state 제거 — 어디서도 read 안 됨 + 매 touchmove re-render 유발 (frame skip 원인)

  // Long press 관련
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const longPressCancelled = useRef(false); // 롱프레스가 취소되었는지 여부

  // Refs
  const menuItemRefs = useRef({});
  const draggedElementRef = useRef(null);
  const draggedElementOriginalHeight = useRef(0); // 드래그 시작 시 원래 높이
  const containerRef = useRef(null);
  const initialScrollTop = useRef(0);
  const touchStartPos = useRef({ x: 0, y: 0 }); // 터치 시작 위치
  const isDraggingRef = useRef(false); // 드래그 중인지 여부 (즉시 체크용)
  // Auto-scroll RAF loop — 손가락이 가장자리 영역에 있는 동안 지속 scroll + transform 보정
  const autoScrollRaf = useRef(null);
  const lastClientY = useRef(0);
  const menuDragStartYRef = useRef(0); // touchStart 시점 손가락 Y (RAF 안에서 사용)
  const draggedMenuInfoRef = useRef(null);

  // 메뉴 아이템 ref 등록
  const setMenuItemRef = (category, index, element) => {
    if (!menuItemRefs.current[category]) {
      menuItemRefs.current[category] = [];
    }
    menuItemRefs.current[category][index] = element;
  };

  // dragged element transform 갱신 — touchmove + RAF auto-scroll 둘 다 호출
  const updateDraggedTransform = (clientY) => {
    if (!draggedElementRef.current) return;
    const currentScrollTop = (document.body.scrollTop || 0) + (document.documentElement.scrollTop || 0);
    const scrollDelta = currentScrollTop - initialScrollTop.current;

    let deltaY = clientY - menuDragStartYRef.current + scrollDelta;

    // 같은 카테고리 boundary 안으로 클램프
    const info = draggedMenuInfoRef.current;
    if (info) {
      const categoryMenus = menuItemRefs.current[info.category] || [];
      const draggedIdx = info.index;
      const itemHeight = draggedElementOriginalHeight.current || 0;
      if (itemHeight > 0 && categoryMenus.length > 0) {
        const minDelta = -(draggedIdx * itemHeight);
        const maxDelta = (categoryMenus.length - 1 - draggedIdx) * itemHeight;
        deltaY = Math.max(minDelta, Math.min(maxDelta, deltaY));
      }
    }
    draggedElementRef.current.style.transform = `translateY(${deltaY}px)`;

    // 다른 메뉴들 위치 갱신
    updateOtherMenuPositions(clientY);
  };

  // Auto-scroll RAF loop — 손가락이 가장자리에 있는 동안 매 frame 마다 scroll + transform 보정
  const startAutoScrollLoop = () => {
    if (autoScrollRaf.current) return;
    const tick = () => {
      if (!isDraggingRef.current) {
        autoScrollRaf.current = null;
        return;
      }
      const clientY = lastClientY.current;
      const winH = window.innerHeight;
      const AUTO_SCROLL_ZONE = 100;
      const AUTO_SCROLL_SPEED = 6;
      let delta = 0;
      if (clientY < AUTO_SCROLL_ZONE) {
        delta = -AUTO_SCROLL_SPEED;
      } else if (clientY > winH - AUTO_SCROLL_ZONE) {
        delta = AUTO_SCROLL_SPEED;
      }
      if (delta === 0) {
        autoScrollRaf.current = null;
        return;
      }
      // body scroll
      const before = document.body.scrollTop + document.documentElement.scrollTop;
      document.body.scrollTop += delta;
      document.documentElement.scrollTop += delta;
      const after = document.body.scrollTop + document.documentElement.scrollTop;
      if (after === before) window.scrollBy(0, delta);

      // dragged element transform 보정 (scrollDelta 변화 반영)
      updateDraggedTransform(clientY);

      autoScrollRaf.current = requestAnimationFrame(tick);
    };
    autoScrollRaf.current = requestAnimationFrame(tick);
  };

  const stopAutoScrollLoop = () => {
    if (autoScrollRaf.current) {
      cancelAnimationFrame(autoScrollRaf.current);
      autoScrollRaf.current = null;
    }
  };

  // Long press 시작
  const handleMenuTouchStart = (e, category, menuIndex, menuItem) => {
    // 이미 다른 long press 진행 중이면 무시
    if (isLongPressing || isDraggingMenu) return;

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    setMenuDragStartY(clientY);

    // 터치 시작 위치 저장
    touchStartPos.current = { x: clientX, y: clientY };

    // 롱프레스 취소 플래그 초기화
    longPressCancelled.current = false;

    // 초기 스크롤 위치 저장 — body 와 documentElement 둘 다 합산 (index.css 의
    // html overflow:hidden + body overflow:auto 구조에서 진짜 scrollable 은 body)
    initialScrollTop.current = (document.body.scrollTop || 0) + (document.documentElement.scrollTop || 0);

    // 0.5초 long press 타이머 (카드 ripple 애니메이션 끝나는 시점과 일치)
    const timer = setTimeout(() => {
      // ⚠️ 중요: 드래그 시작을 즉시 표시 (state 업데이트 전)
      isDraggingRef.current = true;

      // 스크롤 방지를 가장 먼저 실행
      if (containerRef.current) {
        containerRef.current.style.touchAction = 'none';
      }

      // 진동 피드백
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }

      // 모든 리플 효과 제거 (드래그 시작 시)
      document.querySelectorAll('.ripple').forEach(ripple => {
        ripple.remove();
      });

      draggedElementRef.current = menuItemRefs.current[category]?.[menuIndex];

      // 드래그 시작 시 원래 높이 저장
      if (draggedElementRef.current) {
        draggedElementOriginalHeight.current = draggedElementRef.current.getBoundingClientRect().height;
      }

      // State 업데이트 (비동기이므로 나중에)
      setIsLongPressing(true);
      setIsDraggingMenu(true);
      setDraggedMenuInfo({
        category,
        index: menuIndex,
        menuId: menuItem.id || menuItem.db_id,
        prodCd: menuItem.prod_cd,
        element: menuItemRefs.current[category]?.[menuIndex]
      });
    }, 500);

    setLongPressTimer(timer);
  };

  // Long press 취소 (이동 감지)
  const handleMenuTouchMove = (e) => {
    if (longPressTimer && !isDraggingRef.current) {
      // Long press 대기 중 움직임 감지
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;

      const deltaX = Math.abs(clientX - touchStartPos.current.x);
      const deltaY = Math.abs(clientY - touchStartPos.current.y);
      const moveThreshold = 10; // 10px 이상 움직이면 스크롤로 간주

      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        // 스크롤로 판단하여 롱프레스 취소
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
        setIsLongPressing(false);
        longPressCancelled.current = true; // 취소 플래그 설정
        return;
      }
    }

    // ⚠️ 중요: ref를 사용하여 즉시 체크 (state 업데이트 기다리지 않음)
    if (!isDraggingRef.current || !draggedElementRef.current) return;

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    lastClientY.current = clientY;

    // 가장자리 영역이면 RAF auto-scroll loop 시작 (idempotent)
    const AUTO_SCROLL_ZONE = 100;
    const winH = window.innerHeight;
    if (clientY < AUTO_SCROLL_ZONE || clientY > winH - AUTO_SCROLL_ZONE) {
      startAutoScrollLoop();
    } else {
      stopAutoScrollLoop();
    }

    e.preventDefault();
    e.stopPropagation();

    // ref 동기화 — RAF auto-scroll loop 안에서 사용
    menuDragStartYRef.current = menuDragStartY;
    draggedMenuInfoRef.current = draggedMenuInfo;

    updateDraggedTransform(clientY);

    draggedElementRef.current.style.zIndex = '1000';
    draggedElementRef.current.style.opacity = '0.95';
    draggedElementRef.current.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2)';
    draggedElementRef.current.style.backgroundColor = '#ffffff';
    draggedElementRef.current.style.transition = 'none';

    // 현재 카테고리의 다른 메뉴들과 위치 비교
    updateOtherMenuPositions(clientY);
  };

  // 다른 메뉴들의 위치 업데이트
  const updateOtherMenuPositions = (dragCenterY) => {
    if (!draggedMenuInfo) return;

    const { category, index: draggedIndex } = draggedMenuInfo;
    const categoryMenus = menuItemRefs.current[category] || [];

    // 1. 드래그 중인 메뉴가 현재 어느 위치에 있는지 계산
    let targetIndex = draggedIndex;

    for (let i = 0; i < categoryMenus.length; i++) {
      if (i === draggedIndex) continue;

      const element = categoryMenus[i];
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      const threshold = 0.5; // 중심점 기준
      const centerY = rect.top + rect.height * threshold;

      if (dragCenterY < centerY) {
        // 드래그 중심이 이 메뉴 중심보다 위에 있음
        if (i < draggedIndex) {
          // 원래보다 위에 있는 메뉴 → 이 위치가 목표
          targetIndex = i;
          break;
        }
      } else {
        // 드래그 중심이 이 메뉴 중심보다 아래에 있음
        if (i > draggedIndex) {
          targetIndex = i;
        }
      }
    }

    // 2. 각 메뉴를 새 위치로 이동
    categoryMenus.forEach((element, index) => {
      if (!element || index === draggedIndex) return;

      let newIndex = index;

      // 드래그 메뉴가 아래로 이동: 사이의 메뉴들은 위로 한 칸
      if (draggedIndex < targetIndex && index > draggedIndex && index <= targetIndex) {
        newIndex = index - 1;
      }
      // 드래그 메뉴가 위로 이동: 사이의 메뉴들은 아래로 한 칸
      else if (draggedIndex > targetIndex && index < draggedIndex && index >= targetIndex) {
        newIndex = index + 1;
      }

      // 이동 거리 계산: 드래그 중인 메뉴의 빈 공간을 채우기 위해
      if (newIndex !== index) {
        // 드래그 중인 메뉴의 원래 높이에서 상단 패딩(16px) 제외
        const moveDistance = draggedElementOriginalHeight.current - 16;
        const direction = newIndex > index ? 1 : -1; // 아래로(1) 또는 위로(-1)

        element.style.transform = `translateY(${direction * moveDistance}px)`;
        element.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        element.style.zIndex = '100';
      } else {
        // 원위치
        element.style.transform = '';
        element.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        element.style.zIndex = '';
      }
    });
  };

  // 드롭 처리
  const handleMenuTouchEnd = async (e) => {
    // Auto-scroll loop 정지
    stopAutoScrollLoop();

    // Long press 타이머 정리
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    // 롱프레스가 취소되었으면 아무것도 하지 않음
    if (longPressCancelled.current) {
      setIsLongPressing(false);
      longPressCancelled.current = false;
      isDraggingRef.current = false; // ref 초기화
      // 터치 액션 다시 활성화
      if (containerRef.current) {
        containerRef.current.style.touchAction = '';
      }
      return;
    }

    if (!isDraggingMenu || !draggedMenuInfo) {
      setIsLongPressing(false);
      isDraggingRef.current = false; // ref 초기화
      return;
    }

    // 최종 순서 계산
    const newOrder = calculateNewOrder();

    const savedCategory = draggedMenuInfo.category;

    // 즉시 콜백 호출 (state 업데이트로 UI가 재렌더링되면서 자연스럽게 정렬됨)
    if (newOrder && newOrder.length > 0) {

      // 콜백 호출 전 스타일 즉시 초기화 (transition 없이)
      resetAllMenuStyles(true);

      // State 및 ref 초기화
      isDraggingRef.current = false;
      setIsDraggingMenu(false);
      setIsLongPressing(false);
      setDraggedMenuInfo(null);
      draggedElementRef.current = null;

      // 콜백 호출 (state 업데이트) - 이미 transform이 제거된 상태에서 호출
      await onReorderComplete(savedCategory, newOrder);
    } else {

      // State 및 ref 초기화
      isDraggingRef.current = false;
      setIsDraggingMenu(false);
      setIsLongPressing(false);
      setDraggedMenuInfo(null);
      draggedElementRef.current = null;

      // 순서 변경이 없으면 스타일만 초기화
      resetAllMenuStyles();
    }
  };

  // 새로운 순서 계산
  const calculateNewOrder = () => {
    if (!draggedMenuInfo) return [];

    const { category, index: originalIndex } = draggedMenuInfo;
    const categoryMenus = menuItemRefs.current[category] || [];

    // 컨테이너의 위치를 기준점으로 설정
    const container = containerRef.current;
    if (!container) return [];

    const containerRect = container.getBoundingClientRect();

    // 각 메뉴의 상대적 Y 위치 수집 (컨테이너 기준)
    // getBoundingClientRect()는 이미 transform이 적용된 최종 위치를 반환하므로
    // translateY를 추가로 더할 필요 없음
    const positions = categoryMenus.map((element, index) => {
      if (!element) return null;

      const rect = element.getBoundingClientRect();

      // 컨테이너 기준 상대 위치로 계산 (transform은 이미 rect에 반영됨)
      const relativeY = rect.top - containerRect.top;

      return {
        index,
        originalIndex: index,
        y: relativeY,
        element
      };
    }).filter(Boolean);

    // Y 좌표로 정렬
    positions.sort((a, b) => a.y - b.y);

    // 새 순서 배열 생성 (display_order는 0부터 시작)
    return positions.map((pos, newIndex) => {
      const element = pos.element;
      const menuIdRaw = element.getAttribute('data-menu-id');
      const prodCd = element.getAttribute('data-prod-cd');

      // 'menu-82' 형식 prefix 제거 (adminDashboard 가 id: `menu-${menu.id}` 로 저장)
      const menuIdClean = menuIdRaw ? String(menuIdRaw).replace(/^menu-/, '') : null;
      const menuIdInt = menuIdClean ? parseInt(menuIdClean, 10) : null;

      return {
        menuId: Number.isNaN(menuIdInt) ? null : menuIdInt,
        prodCd,
        display_order: newIndex
      };
    });
  };

  // 모든 메뉴 스타일 초기화
  const resetAllMenuStyles = (immediate = false) => {
    Object.values(menuItemRefs.current).forEach(categoryMenus => {
      if (!Array.isArray(categoryMenus)) return;

      categoryMenus.forEach(element => {
        if (!element) return;

        if (immediate) {
          // 즉시 초기화 (transition 없이)
          element.style.transition = 'none';
          element.style.transform = '';
          element.style.zIndex = '';
          element.style.opacity = '';
          element.style.boxShadow = '';
          element.style.backgroundColor = '';
          element.style.paddingTop = '';

          // transition: none을 유지 (원복하지 않음)
          // React 재렌더링 후 자연스럽게 CSS 클래스가 다시 적용됨
        } else {
          element.style.transform = '';
          element.style.transition = '';
          element.style.zIndex = '';
          element.style.opacity = '';
          element.style.boxShadow = '';
          element.style.backgroundColor = '';
          element.style.paddingTop = '';
        }
      });
    });

    // 컨테이너 터치 액션 다시 활성화
    if (containerRef.current) {
      containerRef.current.style.touchAction = '';
    }
  };

  // 롱프레스 대기 중 스크롤 감지 리스너
  useEffect(() => {
    if (!longPressTimer) return;

    const handleMove = (e) => handleMenuTouchMove(e);
    const handleEnd = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
        setIsLongPressing(false);
        longPressCancelled.current = true; // 취소 플래그 설정
      }
    };

    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);

    return () => {
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };
  }, [longPressTimer]);

  // 드래그 중 window 이벤트 리스너
  useEffect(() => {
    if (!isDraggingMenu) return;

    const handleMove = (e) => handleMenuTouchMove(e);
    const handleEnd = (e) => handleMenuTouchEnd(e);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDraggingMenu, draggedMenuInfo, menuDragStartY]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
      resetAllMenuStyles();
    };
  }, []);

  return {
    isDraggingMenu,
    isLongPressing,
    draggedMenuInfo,
    setMenuItemRef,
    setContainerRef: (ref) => { containerRef.current = ref; },
    handleMenuTouchStart,
    handleMenuTouchMove,
    handleMenuTouchEnd,
  };
};
